/**
 * One client's survey history, built twice where it has to be: once for the occurrence a pane
 * REPORTS, once for the form its chart strip DRAWS.
 *
 * The two differ only when the reported occurrence belongs to a one-off form — an intake has no
 * series worth a strip — and `chartStripOccurrenceId` then names the cadenced occurrence the strip
 * stands on instead. The client's Ankiety tab and the review inbox on `/raporty` both read this, so
 * the two surfaces cannot disagree about which strip stands over which submission.
 *
 * `chartRange` reaches the series and nothing else (see `model/surveyHistory`).
 */

import { useTranslation } from 'react-i18next';

import {
  buildSurveyHistory,
  chartStripOccurrenceId,
  type SurveyHistorySource,
  type SurveyHistoryView,
} from '../model/surveyHistory';
import { useSurveyHistorySource } from './useSurveyHistorySource';

export interface SurveyHistoryViewsQuery {
  readonly chartRange: { readonly from: string; readonly to: string };
  /** The occurrence the report shows. `null` opens on the newest answered one. */
  readonly occurrenceId: number | null;
  /** The cadenced occurrence the strip was last moved to, or `null`. */
  readonly lastCadencedId: number | null;
}

export interface SurveyHistoryViews {
  /** The report's form, every occurrence of it. */
  readonly view: SurveyHistoryView;
  /** The strip's form. The same object as `view` unless the report is on a one-off form. */
  readonly strip: SurveyHistoryView;
  /** The narrowed read itself, for a caller that projects it further. `null` until it lands. */
  readonly source: SurveyHistorySource | null;
  readonly pending: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
}

export function useSurveyHistoryViews(
  clientId: number,
  query: SurveyHistoryViewsQuery,
): SurveyHistoryViews {
  const { t, i18n } = useTranslation();
  // The names come off the wire in the CONTENT language, which the source hook reads for itself;
  // `i18n.language` only words and formats what the reader sees.
  const { source, exerciseNames, pending, failed, retry } = useSurveyHistorySource(clientId);

  const deps = { t, locale: i18n.language, chartRange: query.chartRange, exerciseNames };
  const view = buildSurveyHistory(source, { ...deps, occurrenceId: query.occurrenceId });
  const stripId = chartStripOccurrenceId(source, query.occurrenceId, query.lastCadencedId);

  return {
    view,
    strip:
      stripId === (view.selected?.id ?? null)
        ? view
        : buildSurveyHistory(source, { ...deps, occurrenceId: stripId }),
    source,
    pending,
    failed,
    retry,
  };
}
